import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import slugify from 'slugify';
import {
  mapIssueToSavedIssue,
  exportIssues,
  jUnitXmlExport
} from '@deque/axe-devtools-issues-utils';
import {
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  RadioGroup,
  Button
} from '@deque/cauldron-react';

import type { Test } from '../../common/api-client';
import useSharedTestAnalyticsData from '../hooks/useSharedTestAnalyticsData';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import useIssuesFilter from '../../common/hooks/useIssuesFilter';
import type { TestRecordIssue } from '@deque/axe-devtools-issues-utils';
import { getApiServerUrl } from '../../common/utils/get-api-server-url';

interface ExportIssuesModalProps {
  issues: TestRecordIssue[];
  test: Test;
  onClose: () => void;
}

type ExportIssue = ReturnType<typeof mapIssueToSavedIssue> & {
  testUrl: string;
  testPageTitle: string;
  axeVersion: string;
};
type Schema = 'axe-devtools' | 'axe-reporter';
type Format = 'json' | 'csv' | 'xml';

const FILENAME_LIMIT = 64;
function getExportFilename(test: Test): string {
  const date = new Date().toISOString().substr(0, 10);
  let host;
  try {
    host = new URL(test.url).host;
  } catch (ex) {
    // ignore if the url can't be parsed
  }

  return `${[host, test.name]
    .filter(Boolean)
    .map(str => slugify(str as string, { strict: true }))
    .join('-')
    .substr(0, FILENAME_LIMIT)}-${date}`;
}

export default function ExportIssuesModal({
  issues,
  test,
  onClose
}: ExportIssuesModalProps) {
  const { t } = useTranslation();
  const [schema, setSchema] = useState<Schema>('axe-devtools');
  const [format, setFormat] = useState<Format>('json');
  const { filters } = useIssuesFilter([]);
  const { ready: analyticsReady, data: analyticsData } =
    useSharedTestAnalyticsData();

  const analytics = useAxeDevtoolsProAnalytics();

  const handleSubmit = () => {
    const mappedIssues: ExportIssue[] = issues.map(issue =>
      mapIssueToSavedIssue(
        { ...issue },
        {
          testUrl: test.url,
          // Note: we don't have access to the original test page title
          testPageTitle: '',
          axeVersion: test.metadata?.axeVersion || '',
          shareURL: `https://${location.host}/issues/${issue.id}`
        }
      )
    );

    const filename = getExportFilename(test);
    if (schema === 'axe-reporter') {
      exportIssues({
        filename: `${filename}.csv`,
        issues: mappedIssues,
        format: 'csv-axe-reports'
      });
    } else if (format === 'json') {
      exportIssues({
        filename: `${filename}.json`,
        issues: mappedIssues,
        format: 'json',
        options: { baseScreenshotURL: getApiServerUrl() }
      });
    } else if (format === 'csv') {
      exportIssues({
        filename: `${filename}.csv`,
        issues: mappedIssues,
        format: 'csv-axe-devtools',
        options: { baseScreenshotURL: getApiServerUrl() }
      });
    } else if (format === 'xml') {
      jUnitXmlExport({
        filename: `${filename}.xml`,
        issues: mappedIssues,
        additionalProperties: {
          ruleset: filters.configAccessibilityStandard,
          name: test.name
        }
      });
    }

    if (analyticsReady) {
      analytics.sharedTestIssuesExport({
        ...analyticsData,
        exportFormat: format,
        exportSchema:
          schema === 'axe-reporter' ? 'axeDevToolsReporter' : 'axeDevToolsPro'
      });
    }

    onClose();
  };

  const handleSchemaChange = ({
    target
  }: React.ChangeEvent<HTMLSelectElement>) => {
    if (target.value === 'axe-reporter') {
      // Reporter only supports csv format
      setFormat('csv');
    }

    setSchema(target.value as Schema);
  };
  const handleFormatChange = ({ value }: { value: Format }) => setFormat(value);
  const exportSchemaOptions = [
    {
      key: 'axe-devtools',
      value: 'axe-devtools',
      label: t('axe DevTools Extension (default)')
    },
    {
      key: 'axe-reporter',
      value: 'axe-reporter',
      label: t('axe DevTools Reporter')
    }
  ];

  return (
    <Modal
      show
      heading={{
        text: t('Export'),
        level: 2
      }}
      onClose={onClose}
    >
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <Select
            label={t('Choose export schema')}
            options={exportSchemaOptions}
            onChange={handleSchemaChange}
            value={schema}
            required
          />
          <h3 id="export-format">{t('Choose export format')}</h3>
          <RadioGroup
            name="export-format"
            aria-labelledby="export-format"
            value={format}
            /* @ts-expect-error cauldron does not export the type we need here :( */
            onChange={handleFormatChange}
            radios={[
              {
                id: 'json',
                value: 'json',
                label: t('JSON'),
                disabled: schema === 'axe-reporter'
              },
              {
                id: 'csv',
                value: 'csv',
                label: t('CSV')
              },
              {
                id: 'xml',
                value: 'xml',
                label: t('JUnit XML'),
                disabled: schema === 'axe-reporter'
              }
            ]}
          />
        </form>
      </ModalContent>
      <ModalFooter>
        <Button onClick={handleSubmit}>{t('Export')}</Button>
        <Button variant="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
